import { useEffect, useState } from "react";
import { bufferedEnd } from "./videoPlayerRules";

const EVENTS = [
  "loadstart", "emptied", "loadedmetadata", "durationchange", "loadeddata", "canplay", "canplaythrough",
  "play", "playing", "pause", "waiting", "seeking", "seeked", "timeupdate", "progress",
  "ratechange", "volumechange", "ended", "error",
];

/** Events after which the element has the data it needs (or has stopped asking). */
const SETTLED = new Set(["playing", "canplay", "canplaythrough", "pause", "seeked", "ended", "error", "emptied", "loadstart"]);

const INITIAL = {
  paused: true,
  ended: false,
  waiting: false,
  seeking: false,
  currentTime: 0,
  duration: NaN,
  buffered: 0,
  volume: 1,
  muted: false,
  rate: 1,
  error: null,
  hasPlayed: false,
};

/**
 * useMediaState — a <video>'s playback state as React state.
 *
 * The element is the single source of truth: every event re-reads it whole
 * rather than patching one field, so the controls can never drift from what
 * the element is actually doing (a pause from the protection hook, a marker
 * prompt, the OS media keys all show up here without being told about).
 *
 * `waiting` is the one thing the element does not expose as a property — it is
 * raised by `waiting` and cleared by any event that means the data
 * arrived or playback stopped asking for it.
 *
 * @param {HTMLVideoElement|null} video  the element, as state (not a ref), so
 *                                       the subscription follows a remount
 */
export default function useMediaState(video) {
  const [state, setState] = useState(INITIAL);

  useEffect(() => {
    if (!video) {
      setState(INITIAL);
      return undefined;
    }
    let waiting = false;
    let hasPlayed = false;

    const read = (e) => {
      const type = e && e.type;
      if (type === "waiting") waiting = true;
      else if (SETTLED.has(type)) waiting = false;
      if (type === "play" || type === "playing") hasPlayed = true;
      setState({
        paused: video.paused,
        ended: video.ended,
        waiting,
        seeking: video.seeking,
        currentTime: video.currentTime || 0,
        duration: video.duration,
        buffered: bufferedEnd(video.buffered, video.currentTime || 0),
        volume: video.volume,
        muted: video.muted,
        rate: video.playbackRate || 1,
        error: video.error ? video.error.code || 1 : null,
        hasPlayed,
      });
    };

    EVENTS.forEach((name) => video.addEventListener(name, read));
    read();
    return () => EVENTS.forEach((name) => video.removeEventListener(name, read));
  }, [video]);

  return state;
}
